import PaginationFooter from './PaginationFooter';
import type { Transaction } from '../types';
import TransactionItem from './TransactionItem';

interface TransactionListProps {
  transactions: Transaction[];
  totalCount?: number;
  loadedCount?: number;
  title?: string;
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onRetryUsd?: () => void;
  paginationError?: string | null;
  onRetry?: () => void;
}

export default function TransactionList({
  transactions,
  totalCount = 0,
  loadedCount = 0,
  title = 'Sold evidence',
  isLoading = false,
  onLoadMore,
  hasMore = false,
  onRetryUsd,
  paginationError,
  onRetry,
}: TransactionListProps) {
  const rawOnlyTransactions = transactions.filter((transaction) => (
    Number.isFinite(transaction.price)
    && !Number.isFinite(transaction.priceUsd)
  ));
  const retryableFxCount = rawOnlyTransactions.filter((transaction) => Boolean(transaction.currency)).length;
  const missingCurrencyCount = rawOnlyTransactions.length - retryableFxCount;

  if (!isLoading && transactions.length === 0) {
    return (
      <section className="rounded-[24px] border border-border bg-white px-6 py-14 text-center shadow-sm">
        <h2 className="text-lg font-semibold text-primary">No sold evidence for this market</h2>
        <p className="mt-2 text-sm text-secondary">Try the All markets filter or check again after new marketplace data is indexed.</p>
      </section>
    );
  }

  return (
    <section aria-labelledby="sold-evidence-heading">
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow text-cyan">Marketplace records</p>
          <h2 id="sold-evidence-heading" className="mt-2 text-xl font-semibold tracking-[-0.035em] text-primary">{title}</h2>
        </div>
        {totalCount > 0 && <span className="text-xs text-secondary">{loadedCount.toLocaleString()} of {totalCount.toLocaleString()} loaded</span>}
      </div>

      {retryableFxCount > 0 && onRetryUsd && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber/25 bg-amber/10 px-4 py-3" role="status">
          <p className="text-xs text-amber-900">{retryableFxCount} loaded sale(s) retain raw prices while an FX rate is unavailable.</p>
          <button onClick={onRetryUsd} className="rounded-xl border border-amber/30 bg-white px-4 py-2 text-xs font-bold text-amber-900">Retry evidence USD</button>
        </div>
      )}
      {missingCurrencyCount > 0 && (
        <div className="mb-4 rounded-2xl border border-border bg-background-secondary px-4 py-3 text-xs text-secondary" role="status">
          {missingCurrencyCount} loaded sale(s) show raw values only because their source currency metadata is missing.
        </div>
      )}

      <div className="space-y-3">
        {transactions.map((transaction) => <TransactionItem key={transaction.soldId} transaction={transaction} />)}
      </div>

      <PaginationFooter hasMore={hasMore} isLoading={isLoading} error={paginationError}
        itemCount={transactions.length} onLoadMore={onLoadMore} onRetry={onRetry}
        loadingTitle="Loading more sold evidence…" finishedTitle="All loaded evidence shown" />
    </section>
  );
}
